<template>
  <public-layout>
    <!-- Cover Banner -->
    <div class="profile-banner">
      <div class="banner-dots"></div>
    </div>

    <div class="sc-container">
      <!-- Profile Header Card -->
      <div class="profile-header-card">
        <div class="profile-avatar-wrap">
          <img
            :src="profileAvatarSrc"
            class="profile-avatar"
            :alt="details.name || ''"
            @error="onAvatarError"
          />
        </div>
        <div class="profile-info">
          <h1 class="profile-name">{{ profileName }}</h1>
          <p v-if="details.profession" class="profile-profession">{{ details.profession }}</p>
          <p v-if="details.city || details.state" class="profile-location">
            <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {{ details.city ? details.city.name : '' }}{{ details.city && details.state ? ', ' : '' }}{{ details.state ? details.state.name : '' }}
          </p>
        </div>
        <div class="profile-actions">
          <button
            v-if="loggedInId && details.id && String(loggedInId) !== String(details.id)"
            class="btn-primary"
            :disabled="followLoading"
            @click="follow(details.id)"
          >
            <span v-if="followLoading" class="sc-spinner"></span>
            <span v-else>Follow</span>
          </button>
          <router-link
            v-if="loggedInId && details.id && String(loggedInId) !== String(details.id)"
            :to="{ path: '/messages', query: { with: details.id } }"
            class="btn-outline"
          >Message</router-link>
          <router-link
            v-if="loggedInId && details.id && String(loggedInId) === String(details.id)"
            to="/profile/update"
            class="btn-outline"
          >Edit Profile</router-link>
          <router-link
            v-if="loggedInId && details.id && String(loggedInId) === String(details.id) && details.company"
            :to="'/company/'+details.company.slug"
            class="btn-outline"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
            My Company
          </router-link>
          <router-link
            v-if="loggedInId && details.id && String(loggedInId) === String(details.id) && !details.company"
            to="/register-company"
            class="btn-primary"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Register Company
          </router-link>
        </div>
      </div>

      <!-- Stats bar -->
      <div class="profile-stats">
        <div class="stat-item">
          <span class="stat-value">{{ projects.length }}</span>
          <span class="stat-label">Projects</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value">{{ properties.length }}</span>
          <span class="stat-label">Properties</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value">{{ plans.length }}</span>
          <span class="stat-label">Concepts</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <span class="stat-value">{{ followers.length }}</span>
          <span class="stat-label">Followers</span>
        </div>
        <template v-if="connections.length">
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-value">{{ connections.length }}</span>
            <span class="stat-label">Connections</span>
          </div>
        </template>
        <template v-if="details.company">
          <div class="stat-divider"></div>
          <div class="stat-item stat-company">
            <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
            <span>{{ details.company.name }}</span>
          </div>
        </template>
      </div>

      <!-- Body grid -->
      <div class="profile-body">
        <!-- Sidebar -->
        <aside class="profile-aside">
          <div class="aside-card">
            <h3 class="aside-title">About</h3>
            <p v-if="details.about" class="aside-about">{{ details.about }}</p>
            <p v-else class="aside-empty">No bio yet.</p>
            <ul class="aside-meta">
              <li v-if="details.city">
                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {{ details.city.name }}{{ details.state ? ', ' + details.state.name : '' }}
              </li>
              <li v-if="details.is_professional" class="professional-badge">
                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                Verified Professional
              </li>
            </ul>
          </div>

          <!-- Companies -->
          <div v-if="companies.length" class="aside-card">
            <h3 class="aside-title">
              <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="margin-right:.375rem;vertical-align:-1px"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
              Companies
            </h3>
            <div class="aside-companies">
              <a
                v-for="co in displayedCompanies"
                :key="co.id"
                :href="'/company/' + co.slug"
                class="aside-company"
              >
                <img
                  v-if="co.logo"
                  :src="imgUrl + 'company/' + co.logo"
                  :alt="co.name"
                  class="aside-company__logo"
                  @error="e => e.target.src = initAvatar(co.name, 40)"
                />
                <img
                  v-else
                  :src="initAvatar(co.name, 40)"
                  :alt="co.name"
                  class="aside-company__logo aside-company__logo--avatar"
                />
                <div class="aside-company__info">
                  <span class="aside-company__name">{{ co.name }}</span>
                  <span v-if="co.city || co.state" class="aside-company__loc">
                    {{ co.city ? co.city.name : '' }}{{ co.city && co.state ? ', ' : '' }}{{ co.state ? co.state.name : '' }}
                  </span>
                </div>
              </a>
            </div>
            <button v-if="companies.length > 3" class="aside-toggle" @click="showAllCompanies = !showAllCompanies">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline v-if="showAllCompanies" points="18 15 12 9 6 15"/>
                <polyline v-else points="6 9 12 15 18 9"/>
              </svg>
              {{ showAllCompanies ? 'View less' : 'View ' + (companies.length - 3) + ' more' }}
            </button>
          </div>
        </aside>

        <!-- Main -->
        <main class="profile-main">
          <!-- Loading state -->
          <div v-if="loading" class="profile-loading">
            <div v-for="n in 3" :key="n" class="skeleton-card">
              <div class="skeleton" style="height:140px;border-radius:8px;margin-bottom:.75rem;"></div>
              <div class="skeleton" style="height:16px;width:70%;border-radius:4px;margin-bottom:.5rem;"></div>
              <div class="skeleton" style="height:12px;width:50%;border-radius:4px;"></div>
            </div>
          </div>

          <template v-else>
            <div class="filter-tabs">
              <button :class="['tab-btn', activeTab === 'projects' && 'tab-btn--active']" @click="activeTab = 'projects'">
                Projects <span class="tab-count">{{ projects.length }}</span>
              </button>
              <button :class="['tab-btn', activeTab === 'properties' && 'tab-btn--active']" @click="activeTab = 'properties'">
                Properties <span class="tab-count">{{ properties.length }}</span>
              </button>
              <button :class="['tab-btn', activeTab === 'plans' && 'tab-btn--active']" @click="activeTab = 'plans'">
                Concepts <span class="tab-count">{{ plans.length }}</span>
              </button>
              <button :class="['tab-btn', activeTab === 'followers' && 'tab-btn--active']" @click="activeTab = 'followers'">
                Followers <span class="tab-count">{{ followers.length }}</span>
              </button>
              <button v-if="connections.length" :class="['tab-btn', activeTab === 'connections' && 'tab-btn--active']" @click="activeTab = 'connections'">
                Connections <span class="tab-count">{{ connections.length }}</span>
              </button>
            </div>

            <!-- Projects tab -->
            <div v-if="activeTab === 'projects'">
              <div v-if="!projects.length" class="empty-state">
                <svg width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                <p>No projects yet.</p>
              </div>
              <div v-else class="listing-grid">
                <a
                  v-for="item in projects"
                  :key="item.id"
                  :href="'/listing/project/' + item.slug"
                  class="listing-card"
                >
                  <div class="listing-card__img-wrap">
                    <img
                      v-if="item.images && item.images.length"
                      :src="item.images[0].path || (imgUrl + item.images[0].image)"
                      :alt="item.name"
                      @error="onImgError"
                    />
                    <div v-else class="listing-card__no-img">Project</div>
                  </div>
                  <div class="listing-card__body">
                    <span class="listing-card__badge project">Project</span>
                    <h4>{{ item.name }}</h4>
                    <p v-if="item.city || item.country" class="listing-card__meta">
                      <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {{ item.city ? item.city.name : '' }}{{ item.city && item.country ? ', ' : '' }}{{ item.country ? item.country.name : '' }}
                    </p>
                    <p class="listing-card__desc">{{ truncate(item.description || item.about) }}</p>
                    <div class="listing-card__footer">
                      <span>
                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                        {{ item.likes ? item.likes.length : 0 }}
                      </span>
                      <span>
                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                        {{ item.comments ? item.comments.length : 0 }}
                      </span>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <!-- Properties tab -->
            <div v-if="activeTab === 'properties'">
              <div v-if="!properties.length" class="empty-state">
                <svg width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                <p>No properties yet.</p>
              </div>
              <div v-else class="listing-grid">
                <a
                  v-for="item in properties"
                  :key="item.id"
                  :href="'/listing/property/' + item.slug"
                  class="listing-card"
                >
                  <div class="listing-card__img-wrap">
                    <img
                      v-if="item.images && item.images.length"
                      :src="item.images[0].path || (imgUrl + item.images[0].image)"
                      :alt="item.name"
                      @error="onImgError"
                    />
                    <div v-else class="listing-card__no-img">Property</div>
                  </div>
                  <div class="listing-card__body">
                    <span class="listing-card__badge property">Property</span>
                    <h4>{{ item.name }}</h4>
                    <p v-if="item.city || item.country" class="listing-card__meta">
                      <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {{ item.city ? item.city.name : '' }}{{ item.city && item.country ? ', ' : '' }}{{ item.country ? item.country.name : '' }}
                    </p>
                    <p class="listing-card__desc">{{ truncate(item.description || item.about) }}</p>
                    <div class="listing-card__footer">
                      <span>
                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                        {{ item.likes ? item.likes.length : 0 }}
                      </span>
                      <span>
                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                        {{ item.comments ? item.comments.length : 0 }}
                      </span>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <!-- Plans tab -->
            <div v-if="activeTab === 'plans'">
              <div v-if="!plans.length" class="empty-state">
                <svg width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <p>No concepts yet.</p>
              </div>
              <div v-else class="listing-grid">
                <a
                  v-for="item in plans"
                  :key="item.id"
                  :href="'/listing/plan/' + item.slug"
                  class="listing-card"
                >
                  <div class="listing-card__img-wrap">
                    <img
                      v-if="item.images && item.images.length"
                      :src="item.images[0].path || (imgUrl + item.images[0].image)"
                      :alt="item.name"
                      @error="onImgError"
                    />
                    <div v-else class="listing-card__no-img">Concept</div>
                  </div>
                  <div class="listing-card__body">
                    <span class="listing-card__badge plan">Concept</span>
                    <h4>{{ item.name }}</h4>
                    <p class="listing-card__desc">{{ truncate(item.description || item.about) }}</p>
                    <div class="listing-card__footer">
                      <span>
                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                        {{ item.likes ? item.likes.length : 0 }}
                      </span>
                      <span>
                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                        {{ item.comments ? item.comments.length : 0 }}
                      </span>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <!-- Connections tab -->
            <div v-if="activeTab === 'connections'">
              <div v-if="!connections.length" class="empty-state">
                <svg width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                <p>No project connections yet.</p>
              </div>
              <div v-else class="conn-list">
                <div v-for="(item, index) in connections" :key="index" class="conn-card">
                  <template v-if="item.project">
                    <a :href="'/listing/project/' + item.project.slug" class="conn-card__img-link">
                      <img v-if="item.project.images && item.project.images.length"
                        :src="item.project.images[0].path" class="conn-card__img" alt="" @error="onImgError" />
                      <div v-else class="conn-card__no-img">
                        <svg width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                      </div>
                    </a>
                    <div class="conn-card__body">
                      <span class="listing-card__badge project">Project</span>
                      <a :href="'/listing/project/' + item.project.slug" class="conn-card__title">{{ item.project.name }}</a>
                      <p v-if="item.project.state || item.project.country" class="listing-card__meta">
                        <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {{ item.project.state ? item.project.state.name + ', ' : '' }}{{ item.project.country ? item.project.country.name : '' }}
                      </p>
                      <p v-if="item.project.project_start_date" class="conn-card__dates">
                        <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        {{ formatConnDate(item.project.project_start_date) }}
                        &ndash;
                        {{ item.project.status == 1 ? formatConnDate(item.project.project_end_date) : 'Present' }}
                      </p>
                      <button v-if="item.project.tags && item.project.tags.length" class="conn-card__team-btn" @click="openTeamModal(item.project)">
                        <div class="conn-avatars">
                          <img v-for="tag in item.project.tags.slice(0,5)" :key="tag.id"
                            :src="tag.user && tag.user.avatar ? $avatarSrc(tag.user.avatar, 'users') : initAvatar(tag.user ? tag.user.name : '')"
                            :alt="tag.user ? tag.user.name : ''" class="conn-avatar" @error="e => e.target.src = initAvatar(tag.user ? tag.user.name : '')" />
                        </div>
                        <span>{{ item.project.tags.length }} connection{{ item.project.tags.length !== 1 ? 's' : '' }}</span>
                      </button>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- Followers tab -->
            <div v-if="activeTab === 'followers'">
              <div v-if="!followers.length" class="empty-state">
                <svg width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                <p>No followers yet.</p>
              </div>
              <div v-else class="followers-grid">
                <a
                  v-for="follower in followers"
                  :key="follower.id"
                  :href="follower.user ? '/sc/profile/' + follower.user.slug : '#'"
                  class="follower-card"
                >
                  <img
                    :src="follower.user && follower.user.avatar ? $avatarSrc(follower.user.avatar, 'users') : initAvatar(follower.user ? follower.user.name : '?', 40)"
                    class="follower-avatar"
                    :alt="follower.user ? follower.user.name : ''"
                    @error="e => e.target.src = initAvatar(follower.user ? follower.user.name : '?', 40)"
                  />
                  <div class="follower-info">
                    <span class="follower-name">{{ follower.user ? follower.user.name : 'Unknown' }}</span>
                    <span v-if="follower.user && follower.user.profession" class="follower-prof">{{ follower.user.profession }}</span>
                  </div>
                </a>
              </div>
            </div>
          </template>
        </main>
      </div>
    </div>
    <!-- Team modal -->
    <transition name="prof-fade">
      <div v-if="showTeamModal" class="prof-modal-backdrop" @click.self="showTeamModal = false">
        <div class="prof-modal">
          <div class="prof-modal__header">
            <h3 class="prof-modal__title">{{ currentTeamProject }} — Team</h3>
            <button class="prof-modal__close" @click="showTeamModal = false">
              <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="prof-modal__body">
            <div v-for="tag in currentTeam" :key="tag.id" class="prof-team-row">
              <a :href="'/sc/profile/' + (tag.user ? tag.user.slug : '#')">
                <img :src="tag.user && tag.user.avatar ? $avatarSrc(tag.user.avatar, 'users') : initAvatar(tag.user ? tag.user.name : '')"
                  class="prof-team-avatar" :alt="tag.user ? tag.user.name : ''" @error="e => e.target.src = initAvatar(tag.user ? tag.user.name : '')" />
              </a>
              <div class="prof-team-info">
                <a :href="'/sc/profile/' + (tag.user ? tag.user.slug : '#')" class="prof-team-name">{{ tag.user ? tag.user.name : 'Unknown' }}</a>
                <span class="prof-team-role">{{ tag.user ? tag.user.profession : '' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </public-layout>
</template>

<script>
import PublicLayout from '@/layouts/PublicLayout.vue';
import axios from 'axios';
import { useSEO } from '@/utils/seo.js';

export default {
  name: 'Profile',
  components: { PublicLayout },

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      apiUrl: process.env.VUE_APP_API_URL || '/api/',
      details: {},
      projects: [],
      properties: [],
      plans: [],
      followers: [],
      connections: [],
      companies: [],
      showAllCompanies: false,
      showTeamModal: false,
      currentTeam: [],
      currentTeamProject: '',
      loading: true,
      followLoading: false,
      loggedInId: localStorage.getItem('user'),
      activeTab: 'projects',
    };
  },

  computed: {
    profileAvatarSrc() {
      const co = this.details.company;
      if (co && co.logo) return this.imgUrl + 'company/' + co.logo;
      if (this.details.avatar) return this.$avatarSrc(this.details.avatar, 'users');
      const name = (co && co.name)
        || this.details.name
        || [this.details.fname, this.details.lname].filter(Boolean).join(' ')
        || '?';
      return this.initAvatar(name, 96);
    },

    displayedCompanies() {
      return this.showAllCompanies ? this.companies : this.companies.slice(0, 3);
    },

    profileName() {
      const co = this.details.company;
      return (co && co.name)
        || this.details.name
        || [this.details.fname, this.details.lname].filter(Boolean).join(' ')
        || '?';
    },
  },

  watch: {
    '$route.params.id'() {
      this.loadAll();
    },
  },

  created() {
    this.loadAll();
  },

  methods: {
    loadAll() {
      this.loading = true;
      this.details = {};
      this.projects = [];
      this.properties = [];
      this.plans = [];
      this.followers = [];
      this.connections = [];
      this.companies = [];

      axios(this.apiUrl + 'user/details/' + this.$route.params.id)
        .then(res => {
          if (res.status === 200 && res.data) {
            this.details = res.data;
            useSEO({
              title: res.data.name,
              description: res.data.bio || res.data.about,
              image: res.data.avatar ? (this.imgUrl + 'users/' + res.data.avatar) : null,
              url: window.location.href,
            });
            const uid = res.data.id;
            const calls = [
              this.fetchProjects(uid),
              this.fetchProperties(uid),
              this.fetchPlans(uid),
              this.fetchFollowers(uid),
            ];
            if (localStorage.getItem('token')) {
              calls.push(this.fetchConnections(uid));
              calls.push(this.fetchCompanies(uid));
            }
            return Promise.all(calls);
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    fetchProjects(uid) {
      return axios(this.apiUrl + 'user/fetch-projects/' + uid)
        .then(res => { if (res.status === 200 && res.data) this.projects = res.data; })
        .catch(() => {});
    },

    fetchProperties(uid) {
      return axios(this.apiUrl + 'user/fetch-properties/' + uid)
        .then(res => { if (res.status === 200 && res.data) this.properties = res.data; })
        .catch(() => {});
    },

    fetchPlans(uid) {
      return axios(this.apiUrl + 'user/fetch-plans/' + uid)
        .then(res => { if (res.status === 200 && res.data) this.plans = res.data; })
        .catch(() => {});
    },

    fetchFollowers(uid) {
      return axios(this.apiUrl + 'user/followers/' + uid)
        .then(res => { if (res.status === 200 && res.data) this.followers = res.data; })
        .catch(() => {});
    },

    follow(userId) {
      this.followLoading = true;
      axios.post(this.apiUrl + 'user/follow', {
        user_id: userId,
        followed_by: this.loggedInId,
      })
        .catch(() => {})
        .finally(() => { this.followLoading = false; });
    },

    truncate(text, len = 100) {
      if (!text) return '';
      const div = document.createElement('div');
      div.innerHTML = text;
      const plain = (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
      return plain.length > len ? plain.slice(0, len) + '…' : plain;
    },

    fetchConnections(uid) {
      return axios.get(this.apiUrl + 'user/projects/' + uid)
        .then(res => { if (res && res.data) this.connections = res.data; })
        .catch(() => {});
    },

    fetchCompanies(uid) {
      return axios.get(this.apiUrl + 'user/companies/' + uid)
        .then(res => { if (res && res.data) this.companies = res.data; })
        .catch(() => {});
    },

    openTeamModal(project) {
      this.currentTeamProject = project.name;
      this.currentTeam = project.tags || [];
      this.showTeamModal = true;
    },

    formatConnDate(d) {
      if (!d) return '';
      const date = new Date(d);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    nameColor(name) {
      const palette = ['#4f46e5','#0891b2','#059669','#d97706','#7c3aed','#db2777','#ea580c','#0284c7'];
      let h = 0;
      for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
      return palette[Math.abs(h) % palette.length];
    },

    initAvatar(name, size = 36) {
      const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
      const bg = this.nameColor(name);
      const fs = Math.round(size * 0.38);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="${bg}"/><text x="${size/2}" y="${size*0.64}" font-family="Arial,sans-serif" font-size="${fs}" font-weight="700" fill="#fff" text-anchor="middle">${initials}</text></svg>`;
      return 'data:image/svg+xml;base64,' + btoa(svg);
    },

    onImgError(e) {
      e.target.src = 'https://placehold.co/400x200/e5e7eb/9ca3af?text=No+Image';
    },
    onAvatarError(e) {
      e.target.src = this.initAvatar(this.profileName, 96);
    },
  },
};
</script>

<style scoped>
.profile-banner {
  height: 200px;
  margin-top: 68px;
  background: linear-gradient(135deg, #0a1628 0%, #1e3a5f 60%, #1a4a7a 100%);
  position: relative;
  overflow: hidden;
}
.banner-dots {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(255,255,255,.08) 1px, transparent 1px);
  background-size: 28px 28px;
}
.profile-header-card {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  padding: 1.5rem 2rem;
  margin-top: -80px;
  display: flex;
  align-items: flex-end;
  gap: 1.5rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 2;
}
.profile-avatar-wrap { flex-shrink: 0; margin-top: -48px; }
.profile-avatar {
  width: 96px; height: 96px; border-radius: 50%;
  border: 4px solid #fff; object-fit: cover; box-shadow: var(--shadow);
}
.profile-info { flex: 1; min-width: 0; }
.profile-name { font-size: 1.5rem; font-weight: 700; color: var(--text); margin: 0 0 .25rem; }
.profile-profession { color: var(--text-muted); font-size: .9375rem; margin: 0 0 .25rem; }
.profile-location { display: flex; align-items: center; gap: .375rem; color: var(--text-muted); font-size: .875rem; margin: 0; }
.profile-actions { display: flex; gap: .75rem; flex-wrap: wrap; align-items: center; margin-left: auto; }

.profile-stats {
  display: flex; align-items: center; gap: 1.5rem;
  background: var(--surface); border-radius: var(--radius); box-shadow: var(--shadow-sm);
  padding: .875rem 2rem; margin: 1rem 0;
  border: 1px solid var(--border-light); flex-wrap: wrap;
}
.stat-item { display: flex; flex-direction: column; align-items: center; }
.stat-value { font-size: 1.25rem; font-weight: 700; color: var(--text); }
.stat-label { font-size: .75rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: .05em; }
.stat-item.stat-company { flex-direction: row; gap: .375rem; color: var(--text-muted); font-size: .875rem; }
.stat-divider { width: 1px; height: 2rem; background: var(--border); }

.profile-body { display: grid; grid-template-columns: 260px 1fr; gap: 1.5rem; padding-bottom: 2.5rem; align-items: start; }
.profile-aside { position: sticky; top: 88px; display: flex; flex-direction: column; gap: 1rem; }
.aside-card {
  background: var(--surface); border-radius: var(--radius-lg);
  border: 1px solid var(--border-light); box-shadow: var(--shadow-sm); padding: 1.5rem;
}
.aside-title { font-size: 1rem; font-weight: 700; color: var(--text); margin: 0 0 .75rem; }
.aside-about { font-size: .9375rem; color: var(--text-muted); line-height: 1.6; margin: 0 0 1rem; }
.aside-empty { color: var(--text-muted); font-style: italic; font-size: .875rem; margin: 0 0 1rem; }
.aside-meta { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: .5rem; }
.aside-meta li { display: flex; align-items: center; gap: .375rem; font-size: .875rem; color: var(--text-muted); }
.professional-badge { color: var(--success) !important; font-weight: 600; }

.aside-companies { display: flex; flex-direction: column; gap: .625rem; margin-top: .25rem; }
.aside-company {
  display: flex; align-items: center; gap: .75rem;
  text-decoration: none; color: inherit;
  padding: .5rem; border-radius: var(--radius);
  transition: background .15s;
}
.aside-company:hover { background: var(--surface-3); text-decoration: none; }
.aside-company__logo {
  width: 40px; height: 40px; border-radius: 8px; object-fit: cover;
  flex-shrink: 0; border: 1px solid var(--border-light);
}
.aside-company__logo--avatar { border: none; }
.aside-company__info { display: flex; flex-direction: column; min-width: 0; }
.aside-company__name {
  font-size: .875rem; font-weight: 600; color: var(--text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.aside-company__loc { font-size: .75rem; color: var(--text-muted); }

.aside-toggle {
  display: flex; align-items: center; gap: .375rem; justify-content: center;
  width: 100%; margin-top: .375rem; padding: .5rem;
  background: none; border: 1px solid var(--border-light); border-radius: var(--radius);
  font-size: .8125rem; font-weight: 600; color: var(--primary);
  cursor: pointer; transition: background .15s;
}
.aside-toggle:hover { background: var(--surface-3); }

.profile-main { min-width: 0; }

/* Loading skeletons */
.profile-loading { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 1rem; margin-top: 1rem; }
.skeleton-card { background: var(--surface); border-radius: var(--radius-lg); padding: 1rem; border: 1px solid var(--border-light); }
.skeleton { background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* Tabs */
.filter-tabs { display: flex; gap: 0; border-bottom: 2px solid var(--border-light); margin-bottom: 1.25rem; flex-wrap: wrap; }
.tab-btn {
  background: none; border: none; padding: .625rem 1rem;
  font-size: .9rem; font-weight: 500; color: var(--text-muted);
  cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px;
  transition: color .15s, border-color .15s; display: flex; align-items: center; gap: .375rem;
}
.tab-btn--active { color: var(--primary); border-bottom-color: var(--primary); font-weight: 600; }
.tab-count {
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--surface-3); color: var(--text-muted);
  border-radius: var(--radius-full); font-size: .75rem; font-weight: 700;
  min-width: 20px; height: 20px; padding: 0 6px;
}
.tab-btn--active .tab-count { background: var(--primary); color: #fff; }

/* Listing grid */
.listing-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 1rem; }
.listing-card {
  display: flex; flex-direction: column;
  background: var(--surface); border-radius: var(--radius-lg);
  border: 1px solid var(--border-light); box-shadow: var(--shadow-sm);
  text-decoration: none; color: inherit;
  transition: transform .15s, box-shadow .15s; overflow: hidden;
}
.listing-card:hover { transform: translateY(-2px); box-shadow: var(--shadow); text-decoration: none; color: inherit; }
.listing-card__img-wrap { height: 140px; overflow: hidden; background: var(--surface-3); }
.listing-card__img-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
.listing-card__no-img {
  height: 100%; display: flex; align-items: center; justify-content: center;
  font-size: .875rem; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: .08em;
}
.listing-card__body { padding: .875rem 1rem 1rem; display: flex; flex-direction: column; flex: 1; }
.listing-card__badge {
  display: inline-block; font-size: .6875rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: .06em; padding: .2rem .5rem; border-radius: var(--radius-full);
  margin-bottom: .5rem; width: fit-content;
}
.listing-card__badge.project  { background: #dbeafe; color: #1d4ed8; }
.listing-card__badge.property { background: #dcfce7; color: #15803d; }
.listing-card__badge.plan     { background: #fef3c7; color: #b45309; }
.listing-card__body h4 { font-size: .9375rem; font-weight: 700; color: var(--text); margin: 0 0 .375rem; line-height: 1.3; }
.listing-card__meta { display: flex; align-items: center; gap: .25rem; font-size: .8rem; color: var(--text-muted); margin: 0 0 .5rem; }
.listing-card__desc { font-size: .8125rem; color: var(--text-muted); line-height: 1.5; margin: 0 0 .75rem; flex: 1; }
.listing-card__footer { display: flex; gap: 1rem; font-size: .8rem; color: var(--text-muted); margin-top: auto; }
.listing-card__footer span { display: flex; align-items: center; gap: .25rem; }

/* Empty state */
.empty-state { display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 3rem 1rem; color: var(--text-muted); text-align: center; }
.empty-state svg { opacity: .4; }
.empty-state p { margin: 0; font-size: 1rem; }

/* Followers */
.followers-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; margin-top: .5rem; }
.follower-card {
  display: flex; align-items: center; gap: .75rem;
  background: var(--surface); border-radius: var(--radius); border: 1px solid var(--border-light);
  padding: .875rem; text-decoration: none; color: inherit;
  transition: box-shadow .15s, transform .15s;
}
.follower-card:hover { box-shadow: var(--shadow-sm); transform: translateY(-1px); text-decoration: none; }
.follower-avatar { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.follower-info { display: flex; flex-direction: column; min-width: 0; }
.follower-name { font-weight: 600; font-size: .875rem; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.follower-prof { font-size: .75rem; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.sc-spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  /* Banner */
  .profile-banner { height: 140px; }

  /* Header card */
  .profile-header-card {
    flex-direction: row; flex-wrap: wrap; align-items: flex-start;
    padding: 1rem; gap: 1rem; margin-top: -56px;
  }
  .profile-avatar-wrap { margin-top: -44px; }
  .profile-avatar { width: 80px; height: 80px; }
  .profile-info { flex: 1; min-width: 0; }
  .profile-name { font-size: 1.125rem; }
  .profile-actions { margin-left: 0; width: 100%; gap: .5rem; }
  .profile-actions .btn-primary,
  .profile-actions .btn-outline { flex: 1; justify-content: center; font-size: .875rem; padding: .5rem .75rem; text-align: center; }

  /* Stats — horizontal scroll */
  .profile-stats {
    overflow-x: auto; flex-wrap: nowrap;
    padding: .75rem 1rem; gap: 1.25rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .profile-stats::-webkit-scrollbar { display: none; }
  .stat-item, .stat-divider { flex-shrink: 0; }

  /* Body */
  .profile-body { grid-template-columns: 1fr; gap: 1rem; }
  .profile-aside { position: static; }

  /* Tabs — horizontal scroll */
  .filter-tabs {
    overflow-x: auto; flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch; scrollbar-width: none;
    border-bottom: 2px solid var(--border-light);
  }
  .filter-tabs::-webkit-scrollbar { display: none; }
  .tab-btn { flex-shrink: 0; white-space: nowrap; }

  /* Listing grid */
  .listing-grid { grid-template-columns: 1fr 1fr; }

  /* Followers */
  .followers-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 520px) {
  /* Header — stack avatar + info then actions below */
  .profile-header-card { flex-direction: column; align-items: flex-start; }
  .profile-avatar-wrap { margin-top: -40px; }
  .profile-avatar { width: 72px; height: 72px; }

  /* Grids — single column */
  .listing-grid { grid-template-columns: 1fr; }
  .followers-grid { grid-template-columns: 1fr; }

  /* Connection cards — stack */
  .conn-card { flex-direction: column; }
  .conn-card__img-link { width: 100%; }
  .conn-card__img,
  .conn-card__no-img { width: 100%; height: 180px; }
  .conn-card__body { padding: .75rem; }
}

/* ── Connections tab ── */
.conn-list { display: flex; flex-direction: column; gap: 1rem; }
.conn-card {
  display: flex; gap: 1rem;
  background: var(--surface); border-radius: var(--radius-lg);
  box-shadow: var(--shadow); overflow: hidden;
  transition: box-shadow .2s;
}
.conn-card:hover { box-shadow: var(--shadow-lg); }
.conn-card__img-link { flex-shrink: 0; display: block; width: 140px; }
.conn-card__img { width: 140px; height: 100%; min-height: 100px; object-fit: cover; display: block; }
.conn-card__no-img {
  width: 140px; min-height: 100px; background: var(--surface-3);
  display: flex; align-items: center; justify-content: center; color: var(--border);
}
.conn-card__body { flex: 1; padding: .875rem 1rem .875rem 0; display: flex; flex-direction: column; gap: .35rem; min-width: 0; }
.conn-card__title {
  font-size: .9375rem; font-weight: 700; color: var(--text);
  text-decoration: none; line-height: 1.3; display: block;
}
.conn-card__title:hover { color: var(--primary); }
.conn-card__dates { display: flex; align-items: center; gap: .3rem; font-size: .8125rem; color: var(--text-muted); margin: 0; }
.conn-card__team-btn {
  display: flex; align-items: center; gap: .5rem; margin-top: .25rem;
  background: none; border: none; cursor: pointer; padding: 0; font-family: inherit;
}
.conn-card__team-btn:hover .conn-card__team-btn span { color: var(--primary); }
.conn-card__team-btn span { font-size: .8125rem; font-weight: 600; color: var(--text-muted); }
.conn-avatars { display: flex; }
.conn-avatar {
  width: 28px; height: 28px; border-radius: 50%; object-fit: cover;
  border: 2px solid var(--surface); margin-left: -6px;
}
.conn-avatars .conn-avatar:first-child { margin-left: 0; }

/* ── Team modal ── */
.prof-modal-backdrop {
  position: fixed; inset: 0; z-index: 900;
  background: rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center; padding: 1.5rem;
}
.prof-modal {
  background: var(--surface); border-radius: var(--radius-lg);
  box-shadow: 0 24px 64px rgba(0,0,0,.18);
  width: 100%; max-width: 440px; max-height: calc(100vh - 3rem);
  display: flex; flex-direction: column; overflow: hidden;
}
.prof-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.125rem 1.25rem; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.prof-modal__title { font-size: 1rem; font-weight: 700; color: var(--text); margin: 0; }
.prof-modal__close {
  width: 30px; height: 30px; border-radius: 50%; border: none; background: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--text-muted); transition: background .15s;
}
.prof-modal__close:hover { background: var(--surface-3); color: var(--text); }
.prof-modal__body { padding: .75rem 1.25rem 1.25rem; overflow-y: auto; flex: 1; }
.prof-team-row {
  display: flex; align-items: center; gap: .75rem;
  padding: .625rem 0; border-bottom: 1px solid var(--border-light);
}
.prof-team-row:last-child { border-bottom: none; }
.prof-team-avatar { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.prof-team-info { flex: 1; min-width: 0; }
.prof-team-name { display: block; font-size: .9rem; font-weight: 600; color: var(--text); text-decoration: none; }
.prof-team-name:hover { color: var(--primary); }
.prof-team-role { font-size: .8rem; color: var(--text-muted); }
.prof-fade-enter-active, .prof-fade-leave-active { transition: opacity .2s; }
.prof-fade-enter-from, .prof-fade-leave-to { opacity: 0; }

@media (max-width: 600px) {
  .conn-card__img-link { width: 90px; }
  .conn-card__img, .conn-card__no-img { width: 90px; }
}
</style>
